import axios from 'axios';
import { API_BASE_URL } from '../config/api.ts';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
});

// Flag to prevent multiple refresh attempts at once
let isRefreshing = false;
// Queue of failed requests to retry after token refresh
let failedQueue = [];

// Process the queue of failed requests
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

// Function to refresh the token
const refreshToken = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token available');

        const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        return access_token;
    } catch (error) {
        localStorage.removeItem('token');
        throw error;
    }
};

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If we're already refreshing, add this request to the queue
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return axios(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const token = await refreshToken();
                processQueue(null, token);
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                // Clear token and redirect to root (which will show login page)
                localStorage.removeItem('token');
                window.location.href = '/';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // For other errors or if token refresh failed
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/';
        }

        return Promise.reject(error);
    }
);

export default api;
